/* eslint-disable no-plusplus, no-continue, no-use-before-define, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowRight, ArrowLeft, Download, Check } from 'lucide-react';

import { ShishaMix } from '../types';
import logo from '../assets/logo.png';



interface RecipeCardPopupProps {
  mix: ShishaMix;
  onClose: () => void;
  onConfirm?: () => void;
  confirmText?: string;
}

export default function RecipeCardPopup({
  mix,
  onClose,
  onConfirm,
  confirmText,
}: RecipeCardPopupProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Responsive scale
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // 400px is the card width, 40px is for some margin
      if (width < 440) {
        setScale((width - 40) / 400);
      } else {
        setScale(1);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleClose = () => {
    const animationsEnabled = localStorage.getItem('animationsEnabled') !== 'false';
    if (!animationsEnabled) {
      onClose();
      return;
    }
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 500);
  };

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Payload: encode recipe id and flavor ids with ultra-short keys to maximize dot size
    const qrPayload = JSON.stringify({
      i: mix.id,
      f: mix.flavors.map((fl) => ({
        i: fl.flavor_id,
        r: fl.ratio,
      })),
    });

    import('../utils/qrCodeUtils').then(({ generateQrPlain }) => {
      generateQrPlain(qrPayload).then((dataUrl) => {
        const qrImg = new Image();
        qrImg.onload = () => {
          canvas.width = qrImg.width;
          canvas.height = qrImg.height;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(qrImg, 0, 0, canvas.width, canvas.height);
        };
        qrImg.src = dataUrl;
      }).catch(console.error);
    });
  }, [mix.id]);


  const handleSaveQr = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;

    // Save as PNG (QR codes require lossless format for reliable scanning)
    const url = canvas.toDataURL('image/png');
    const filename = `${mix.title}_${mix.id}_QR.png`;
    import('../utils/imageUtils').then(({ saveImageFile }) => {
      saveImageFile(url, filename).then((success) => {
        if (success) {
          setDownloadSuccess(true);
          setTimeout(() => setDownloadSuccess(false), 2000);
        }
      });
    });

    // Success handled in the promise
  };

  const frontCardRef = useRef<HTMLDivElement>(null);
  const backCardRef = useRef<HTMLDivElement>(null);

  const handleSaveCardImage = async () => {
    if (!frontCardRef.current || !backCardRef.current) return;
    try {
      const captureNode = document.createElement('div');
      captureNode.style.display = 'flex';
      captureNode.style.gap = '40px';
      captureNode.style.position = 'fixed';
      captureNode.style.left = '-9999px';
      captureNode.style.top = '0';
      captureNode.style.padding = '40px';
      captureNode.style.backgroundColor = '#1a1c1f';
      captureNode.style.borderRadius = '20px';

      // Clone Front
      const frontClone = frontCardRef.current.cloneNode(true) as HTMLElement;
      frontClone.style.transform = 'none';
      frontClone.style.position = 'relative';
      frontClone.style.width = '400px';
      frontClone.style.height = '480px';
      frontClone.style.flexShrink = '0';
      captureNode.appendChild(frontClone);

      // Clone Back
      const backClone = backCardRef.current.cloneNode(true) as HTMLElement;
      backClone.style.transform = 'none'; // removes rotateY(180deg)
      backClone.style.position = 'relative';
      backClone.style.width = '400px';
      backClone.style.height = '480px';
      backClone.style.flexShrink = '0';
      captureNode.appendChild(backClone);

      document.body.appendChild(captureNode);

      // Copy canvases for Front
      const originalCanvasesFront = frontCardRef.current.querySelectorAll('canvas');
      const clonedCanvasesFront = frontClone.querySelectorAll('canvas');
      originalCanvasesFront.forEach((canvas, i) => {
        const ctx = clonedCanvasesFront[i].getContext('2d');
        if (ctx) ctx.drawImage(canvas, 0, 0);
      });

      // Copy canvases for Back (QR code)
      const originalCanvasesBack = backCardRef.current.querySelectorAll('canvas');
      const clonedCanvasesBack = backClone.querySelectorAll('canvas');
      originalCanvasesBack.forEach((canvas, i) => {
        const ctx = clonedCanvasesBack[i].getContext('2d');
        if (ctx) ctx.drawImage(canvas, 0, 0);
      });

      await new Promise((resolve) => setTimeout(resolve, 50));
      
      // Clean up ripple effects from cloned buttons
      captureNode.querySelectorAll('.ripple-effect').forEach((el) => el.remove());

      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(captureNode, {
        scale: 2,
        backgroundColor: '#1a1c1f',
        useCORS: true,
        logging: false,
      });

      document.body.removeChild(captureNode);

      const url = canvas.toDataURL('image/png');
      const filename = `hookah-forge-${mix.title.replace(/\\s+/g, '_')}_両面.png`;
      const { saveImageFile } = await import('../utils/imageUtils');
      await saveImageFile(url, filename);
    } catch (err) {
      console.error('Failed to generate card PNG', err);
    }
  };

  const animationsEnabled = localStorage.getItem('animationsEnabled') !== 'false';

  return (
    <div
      className={`modal-backdrop ${isClosing ? 'closing' : ''}`}
      onClick={handleClose}
      style={{
        zIndex: 3000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        animation: !animationsEnabled ? 'none' : (isClosing ? 'fadeOut 0.5s ease-in forwards' : 'fadeIn 0.5s ease-out'),
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(26, 28, 31, 0.85)',
          backdropFilter: 'blur(10px)',
          zIndex: -1,
          pointerEvents: 'none',
        }}
      />
      <div
        className="card-container"
        onClick={(e) => e.stopPropagation()}
        style={{
          '--card-scale': scale,
          animation: !animationsEnabled ? 'none' : (isClosing ? 'modalScaleDown 0.5s cubic-bezier(0.25, 1, 0.5, 1) forwards' : 'none'),
        } as React.CSSProperties}
      >
          <div className={`card-flipper ${isFlipped ? 'flipped' : ''}`}>
          {/* FRONT SIDE (表面) */}
          <div
            ref={frontCardRef}
            className="card-face front"
            style={{
              pointerEvents: isFlipped ? 'none' : 'auto',
            }}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(231, 228, 223, 0.08)',
                borderRadius: '50%',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--color-text)',
              }}
            >
              <X size={18} />
            </button>

            {/* Content Top */}
            <div style={{ marginTop: '20px', textAlign: 'center', width: '100%' }}>
              <span
                style={{
                  fontSize: '11px',
                  color: 'var(--color-accent)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 'bold',
                  display: 'block',
                }}
              >
                ミックスカード
              </span>
              <h2
                style={{
                  fontSize: '28px',
                  fontFamily: 'var(--font-heading-ja)',
                  marginTop: '8px',
                  lineHeight: '1.4',
                  fontWeight: 'bold',
                }}
              >
                {mix.title}
              </h2>
              <span
                style={{
                  fontSize: '13px',
                  color: 'rgba(231, 228, 223, 0.7)',
                  marginTop: '4px',
                  display: 'block',
                }}
              >
                by {mix.creator}
              </span>
            </div>

            {/* Content Middle: Flavor List & Ratios */}
            <div
              style={{
                flexGrow: 1,
                flexShrink: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                gap: '12px',
                margin: '30px 0',
                overflowY: 'auto',
                minHeight: 0,
              }}
            >
              <span
                style={{ fontSize: '11px', color: 'rgba(231, 228, 223, 0.5)' }}
              >
                配合フレーバー
              </span>
              <div
                className="card-flavor-list-scroll"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  maxHeight: '180px',
                  overflowY: 'overlay' as any,
                  paddingRight: '4px',
                }}
              >
                {mix.flavors.map((f) => (
                  <div
                    key={`${f.brand}-${f.name}-${f.ratio}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid rgba(231, 228, 223, 0.05)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '12px',
                          fontFamily: 'var(--font-body)',
                        }}
                      >
                        <span
                          style={{
                            opacity: 0.6,
                            fontSize: '10px',
                            marginRight: '6px',
                          }}
                        >
                          {f.brand}
                        </span>
                        {f.name}
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: 'var(--color-accent)',
                        fontFamily: 'var(--font-numbers)',
                      }}
                    >
                      {f.ratio}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Content Bottom */}
            {onConfirm ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderTop: '1px solid rgba(231, 228, 223, 0.08)',
                  paddingTop: '20px',
                }}
              >
                <button
                  type="button"
                  onClick={handleClose}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(231, 228, 223, 0.3)',
                    color: 'var(--color-text)',
                    padding: '8px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleClose();
                    onConfirm();
                  }}
                  style={{
                    background: 'var(--color-primary)',
                    border: 'none',
                    color: '#fff',
                    padding: '8px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                >
                  {confirmText || '自動入力する'}
                </button>
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  alignItems: 'center',
                  borderTop: '1px solid rgba(231, 228, 223, 0.08)',
                  paddingTop: '20px',
                }}
              >
                {/* Left container */}
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <img
                    src={logo}
                    alt="Hookah Forge Logo"
                    style={{ height: '40px', opacity: 0.8 }}
                  />
                </div>

                {/* Center container */}
                <div />

                {/* Right container */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={handleSaveCardImage}
                    style={{
                      background: 'rgba(231, 228, 223, 0.08)',
                      border: '1px solid rgba(231, 228, 223, 0.15)',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--color-text)',
                      cursor: 'pointer',
                    }}
                    aria-label="画像を保存"
                  >
                    <Download size={18} />
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsFlipped(true)}
                    style={{
                      background: 'rgba(231, 228, 223, 0.08)',
                      border: '1px solid rgba(231, 228, 223, 0.15)',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--color-accent)',
                      cursor: 'pointer',
                    }}
                    aria-label="裏面へ"
                  >
                    <ArrowRight size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* BACK SIDE (裏面) */}
          <div
            ref={backCardRef}
            className="card-face back"
            style={{
              pointerEvents: isFlipped ? 'auto' : 'none',
            }}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(231, 228, 223, 0.08)',
                borderRadius: '50%',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--color-text)',
              }}
            >
              <X size={18} />
            </button>

            {/* Back Header */}
            <div
              style={{ marginTop: '20px', textAlign: 'center', width: '100%' }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-caption)',
                  color: 'var(--color-accent)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 'bold',
                }}
              >
                ミックスカード
              </span>
            </div>

            {/* Back QR Canvas Container */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '20px 0',
                backgroundColor: '#ffffff',
                padding: '12px',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              }}
            >
              <canvas
                ref={canvasRef}
                width={300}
                height={300}
                style={{
                  width: '200px',
                  height: '200px',
                  display: 'block',
                }}
              />
            </div>

            {/* Back Actions */}
            <div
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                borderTop: '1px solid rgba(231, 228, 223, 0.08)',
                paddingTop: '20px',
              }}
            >
              {/* Left container */}
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <button
                  type="button"
                  onClick={() => setIsFlipped(false)}
                  style={{
                    background: 'rgba(231, 228, 223, 0.08)',
                    border: '1px solid rgba(231, 228, 223, 0.15)',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-accent)',
                    cursor: 'pointer',
                  }}
                >
                  <ArrowLeft size={20} />
                </button>
              </div>

              {/* Center container */}
              <div />

              {/* Right container */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleSaveQr}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--color-primary)',
                    color: '#ffffff',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    boxShadow: '0 4px 12px rgba(107, 78, 255, 0.25)',
                    cursor: 'pointer',
                    border: 'none',
                  }}
                  aria-label="QRコードを保存"
                >
                  {downloadSuccess ? <Check size={20} /> : <Download size={20} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
